import { el, clear } from '../../core/dom.js';
import {
  cadenceRadios, clockOf, clip, destOn, monthDayOf, savedPrompts, tzLabel, weekdayToggles,
} from './model.js';

const DAYS = [
  { iso: 1, short: 'Mon' }, { iso: 2, short: 'Tue' }, { iso: 3, short: 'Wed' },
  { iso: 4, short: 'Thu' }, { iso: 5, short: 'Fri' }, { iso: 6, short: 'Sat' },
  { iso: 7, short: 'Sun' },
];

const MONTHS = [['1', '1st'], ['15', '15th'], ['last', 'Last day']];

function legend(n, label) {
  return el('legend', {}, el('span', { class: 'sched-n' }, n), label);
}

function questionBlock(form, saved) {
  const prompts = savedPrompts(saved);
  return el('fieldset', { class: 'sched-block' },
    legend('1', 'Question'),
    prompts.length ? el('label', { class: 'sched-field' },
      el('span', {}, 'Start from a saved question'),
      el('select', { name: 'saved' },
        el('option', { value: '' }, 'Write a new question'),
        prompts.map((prompt) => el('option', { value: prompt }, clip(prompt, 80))))) : null,
    el('label', { class: 'sched-field' },
      el('span', { 'data-prompt-label': 'true' }, 'Or write a new one'),
      el('textarea', {
        id: 'sched-prompt',
        name: 'prompt',
        rows: '4',
        maxlength: '4000',
        required: true,
        placeholder: 'Ask anything Bonnie can answer for this product',
      }, form.prompt)),
    el('label', { class: 'sched-check' },
      el('input', {
        type: 'checkbox',
        name: 'save_question',
        checked: form.save_question ? true : null,
      }),
      el('span', {}, 'Save this question for reuse')));
}

function segment(label, rows) {
  return el('div', { class: 'sched-seg', role: 'radiogroup', 'aria-label': label },
    rows.map((row) => {
      const attrs = {
        type: 'button',
        class: row.on ? 'sched-seg-btn is-on' : 'sched-seg-btn',
        role: 'radio',
        'aria-checked': row.on ? 'true' : 'false',
        tabindex: row.on ? '0' : '-1',
        'data-act': row.act,
      };
      attrs[row.attr] = row.value;
      return el('button', attrs, row.label);
    }));
}

function cadenceBlock(cadence) {
  const labels = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' };
  return segment('When', cadenceRadios(cadence).map((row) => ({
    on: row.checked,
    act: 'cadence',
    attr: 'data-cadence',
    value: row.value,
    label: labels[row.value],
  })));
}

function timeInput(form) {
  return el('input', {
    type: 'time',
    name: 'run_time',
    class: 'sched-input sched-time-input',
    value: clockOf(form.run_time),
    'aria-label': 'Run time',
  });
}

export function paintWhen(host, form) {
  clear(host);
  const time = timeInput(form);
  if (form.cadence === 'weekly') {
    const labels = Object.fromEntries(DAYS.map((day) => [day.iso, day.short]));
    host.append(el('div', { class: 'sched-toggles', role: 'group', 'aria-label': 'Weekdays' },
      weekdayToggles(form.weekdays).map((row) => el('button', {
        type: 'button',
        class: row.pressed ? 'sched-toggle is-on' : 'sched-toggle',
        'aria-pressed': row.pressed ? 'true' : 'false',
        'data-act': 'day',
        'data-iso': String(row.iso),
      }, labels[row.iso]))), time);
    return;
  }
  if (form.cadence === 'monthly') {
    const current = monthDayOf(form.month_day);
    host.append(segment('Day of month', MONTHS.map(([value, label]) => ({
      on: current === value,
      act: 'month',
      attr: 'data-day',
      value,
      label,
    }))), time);
    return;
  }
  host.append(time);
}

function destCheck(key, name, hint, on) {
  return el('label', { class: 'sched-check sched-dest-check' },
    el('input', {
      type: 'checkbox',
      'data-act': 'dest',
      'data-dest': key,
      checked: on ? true : null,
    }),
    el('span', { class: 'sched-check-copy' },
      el('span', { class: 'sched-check-name' }, name),
      el('span', { class: 'sched-check-hint' }, hint)));
}

export function paintEmails(host, recipients) {
  clear(host);
  for (const email of recipients) {
    host.append(el('span', { class: 'sched-chip' },
      email,
      el('button', {
        type: 'button',
        class: 'sched-chip-x',
        'data-act': 'unemail',
        'data-email': email,
        'aria-label': `Remove ${email}`,
      }, '×')));
  }
  host.append(
    el('input', {
      type: 'text',
      name: 'email-new',
      class: 'sched-input sched-email-input',
      placeholder: 'name@company.com',
      autocomplete: 'email',
      'aria-label': 'Recipient email',
    }),
    el('button', { type: 'button', class: 'sched-btn', 'data-act': 'add-email' }, 'Add'),
  );
}

export function paintDest(host, emailHost, form) {
  clear(host);
  const email = destOn(form.destination, 'email');
  const recipientHint = el('p', {
    class: 'sched-hint sched-email-hint',
    hidden: email ? null : true,
  }, 'Only people with access to this product can be recipients.');
  host.append(el('div', { class: 'sched-dest-choices' },
    el('div', { class: 'sched-email-block' },
      destCheck('email', 'Email', 'Send the answer to the people below', email),
      emailHost,
      recipientHint),
    destCheck('kb', 'Documents', 'Save a copy in the Knowledge base', destOn(form.destination, 'kb'))));
  emailHost.hidden = !email;
}

function actionBlock(editing) {
  return el('div', { class: 'sched-form-actions' },
    editing ? el('button', { type: 'button', class: 'sched-btn', 'data-act': 'pause' }, 'Pause') : null,
    editing ? el('button', { type: 'button', class: 'sched-btn', 'data-act': 'delete' }, 'Delete') : null,
    el('button', { type: 'submit', class: 'sched-cta' }, 'Save schedule'),
    el('button', { type: 'button', class: 'sched-btn sched-secondary', 'data-act': 'run' }, 'Run once now'));
}

export function buildForm(form, saved, productName) {
  const name = productName || 'This product';
  const errorNode = el('p', { id: 'sched-error', class: 'sched-form-error', role: 'alert', hidden: true });
  const whenHost = el('div', { class: 'sched-when' });
  const destHost = el('div', { class: 'sched-dest' });
  const emailHost = el('div', { class: 'sched-emails' });
  const destWarn = el('p', { class: 'sched-inline', role: 'alert', hidden: true }, 'Choose Email or Documents.');
  const busyNote = el('p', { class: 'sched-busy', 'aria-live': 'polite', hidden: true });
  const intro = form.id
    ? `${name} · pause, edit, or delete. The full question is below.`
    : `${name} · nothing is created until you save.`;
  const root = el('form', { class: 'sched-form' },
    el('p', { class: 'sched-hint' }, intro),
    errorNode,
    questionBlock(form, saved),
    el('fieldset', { class: 'sched-block' },
      legend('2', 'When'),
      cadenceBlock(form.cadence),
      whenHost,
      el('p', { class: 'sched-hint' }, `Times use ${tzLabel(form.timezone)}.`)),
    el('fieldset', { class: 'sched-block' },
      legend('3', 'Where it lands'),
      destHost,
      destWarn),
    busyNote,
    actionBlock(!!form.id));
  return { root, errorNode, whenHost, destHost, emailHost, destWarn, busyNote };
}
